import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';
import { DefaultTokenContext } from '../../context/DefaultTokenContext';
import { NetworkContext } from '../../context/NetworkContext';

import { Check, X } from 'lucide-react';
import { getTelegramId } from '../../utils/telegramClientData';
import { tgShowAlert } from '../../utils/telegramPopup';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Spin, Button } from 'antd';

import CustomButton from '../Componentization/CustomButton';
import CustomText from '../../styles/CustomText';
import CourierText from '../../styles/CourierText';

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { checkHeaderText } from '../../store';

import useSecureStorage from '../../hooks/useSecureStorage';

import MainPageHeader from '../../nav/MainPageHeader';
import i18next from 'i18next';
import moment from 'moment';
import BigNumber from 'bignumber.js';

interface Wallet {
  id: string;
  type: string;
  amount: string;
  listAmount: string;        // ✅ 항상 문자열
  currency: string;
  listCurrency: string;
  date: any;
  direction: 'in' | 'out';
  transferType: string;
  fee: string;

  // ✅ 내부이체 메타데이터 (seller 기반)
  isInternal?: boolean;
  qPin?: number;
  shopName?: string;
}

interface Asset {
  id: number;
  name: string;
  symbol: string;
  balance: string;
  icon: any;
}

const emptyToken: Asset = {
  id: 0,
  name: '',
  symbol: '',
  balance: '',
  icon: './img/icon_none.png',
};

// 이전 들어왔던 토큰의 심볼
let pre_symbol = '';

// 이전페이지 캐싱 데이터
let pre_cached: Wallet[] = [];

const WalletScreen: React.FC = () => {
  const { theme } = useContext(ThemeContext);
  const { lastSyncTxs, getBalance, getServerTxHistory } = useContext(NetworkContext);

  let navigate = useNavigate();
  let dispatch = useDispatch();
  const { t } = useTranslation();

  const { searchToken } = useContext(DefaultTokenContext);
  const { getWalletAddress, getBlockChain } = useSecureStorage();

  const [loading, setLoading] = useState(false);
  const [accountAddress, setAccountAddress] = useState('');
  const [isDateModalVisible, setDateModalVisible] = useState(false);
  const [isTransactionModalVisible, setTransactionModalVisible] = useState(false);
  const [selectedTransactionOption, setSelectedTransactionOption] = useState(
    i18next.t('template_wallet_screen_message_16'),
  );
  const [selectedStatusOption, setSelectedStatusOption] = useState(
    i18next.t('template_wallet_screen_message_22'),
  );
  const [isStatusModalVisible, setStatusModalVisible] = useState(false);
  const [transactions, setTransactions] = useState<Wallet[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Wallet[]>([]);
  const [startDate, setStartDate] = useState<any>();
  const [endDate, setEndDate] = useState<any>();
  const [selectedDateOption, setSelectedDateOption] = useState<string | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState(new Date());

  const [refreshing, setRefreshing] = useState(false);

  const location = useLocation();
  const [token, setToken] = useState(location.state === null ? null : location.state.token);

  // ---------------- 초기 토큰/캐시 처리 ----------------
  useEffect(() => {
    setToken(emptyToken);
    if (location.state) {
      const newToken = location.state.token;
      setToken(newToken);
      if (pre_symbol === newToken.symbol) {
        setTransactions(pre_cached);
        setFilteredTransactions(pre_cached);
      } else {
        setTransactions([]);
        setFilteredTransactions([]);
        pre_cached = [];
      }
      pre_symbol = newToken.symbol;
    }
  }, [location.state]);

  useEffect(() => {
    if (!token) return;
    const title = token.symbol.toUpperCase();
    dispatch(
      checkHeaderText({
        link: 'WalletScreen',
        title: title + ' ' + t('template_wallet_screen_message_01'),
      }),
    );
  }, [token]);

  useEffect(() => {
    const fetchAccountDetails = async () => {
      const walletAddress = await getWalletAddress();
      if (walletAddress) {
        setAccountAddress(walletAddress);
      }
    };
    fetchAccountDetails();
  }, []);

  // 잔액 갱신
  useEffect(() => {
    const refreshBalanceAndTransactions = async () => {
      if (accountAddress && token) {
        try {
          if (token.symbol === process.env.REACT_APP_NETWORK_CURRENCY) {
            const balance = await getBalance(accountAddress);
            setToken((prevToken: any) => ({
              ...prevToken,
              balance: `${parseFloat(balance.toString() || '0').toString()}`,
            }));
          }
        } catch (error) {
          console.log('Error refreshing balance and transactions:', error);
        }
      }
    };

    if (token) refreshBalanceAndTransactions();
  }, [accountAddress, token?.symbol]);

  useEffect(() => {
    fetchServerTxHistory();
  }, [lastSyncTxs]);

  // ---------------- 서버 TX 조회 ----------------
  const fetchServerTxHistory = async () => {
    if (!token) return;
    console.warn(`WalletScreen fetchServerTxHistory 시작`);

    const selNetwork = await getBlockChain();
    const telegramId = await getTelegramId();

    if (selNetwork === '') {
      console.warn(`fetchServerTxHistory 잘못된 네트워크 정보`);
      await tgShowAlert(t('balance_screen_message_05'));
      return;
    }
    if (telegramId === '') {
      console.warn(`fetchServerTxHistory 잘못된 텔레그램 유저 정보`);
      await tgShowAlert(t('balance_screen_message_10'));
      return;
    }

    if (!accountAddress) {
      console.warn(`fetchServerTxHistory 지갑주소 정보 없음`);
      return;
    }

    const selectTokenInfo = await searchToken(token.symbol);
    console.log(`WalletScreen fetchServerTxHistory selectTokenInfo : `, selectTokenInfo);

    let transactionsWithId: Wallet[] | null = null;
    let history = null;

    if (selectTokenInfo?.type === 'coin') {
      history = await getServerTxHistory(accountAddress, selNetwork, telegramId, 'alltx', 1, 500);
      if (history.items) {
        transactionsWithId = history.items
          .map((tx: any, index: number) =>
            mapCoinTransactionToWallet(tx, accountAddress, index),
          )
          .filter(Boolean) as Wallet[];
      }
    } else if (selectTokenInfo?.type === 'token') {
      history = await getServerTxHistory(
        accountAddress,
        selNetwork,
        telegramId,
        token.symbol,
        1,
        500,
      );
      if (history.items) {
        transactionsWithId = history.items.map((tx: any, index: number) =>
          mapTokenTransactionToWallet(tx, accountAddress, index),
        ) as Wallet[];
      }
    }

    setTransactions(transactionsWithId || []);
    setFilteredTransactions(transactionsWithId || []);
    pre_cached = transactionsWithId || [];
  };

  // ---------------- 맵핑: 코인 ----------------
  const mapCoinTransactionToWallet = (
    tx: any,
    accountAddress: string,
    index: number,
  ): Wallet | null => {
    const toAddress = tx.to ? tx.to.hash || tx.to : null;
    const fromAddress = tx.from ? tx.from.hash || tx.from : null;
    let fee = '0';
    const date = new Date(tx.timestamp * 1000).toISOString().split('T')[0];
    const transferType = tx.transfer_type;
    let txId = '';
    let txType = '';
    const coinType = tx.coin_type;

    // 내부이체 메타데이터 (seller)
    const rawQPin = tx.seller?.qpin ?? null;
    const rawShopName = tx.seller?.seller_name ?? null;

    if (tx.transaction_hash != null && tx.transaction_hash !== 'undefined') {
      txId = tx.transaction_hash;
    } else {
      txId = tx.hash;
    }

    if (tx.fee?.value != null && tx.fee?.value !== 'undefined') {
      fee = tx.fee.value.toString();
    }

    if (transferType.toLowerCase() === 'withdrawal') {
      txType = i18next.t('template_wallet_screen_message_09');
    } else {
      txType = i18next.t('template_wallet_screen_message_10');
    }

    let txData: Wallet | null = null;

    if (coinType === 'coin' && transferType.toLowerCase() === 'withdrawal') {
      if (fromAddress === accountAddress && toAddress === accountAddress) {
        txData = {
          id: `${txId}-${index}`,
          type: txType,
          amount: tx.value?.toString() ?? '0',
          listAmount: ((+tx.value || 0) + (+fee || 0)).toString(),
          currency: token.symbol,
          listCurrency: token.symbol,
          date,
          direction: 'out',
          transferType,
          fee,
        };
      } else if (fromAddress !== accountAddress && toAddress === accountAddress) {
        txData = {
          id: `${txId}-${index}`,
          type: txType,
          amount: tx.value?.toString() ?? '0',
          listAmount: ((+tx.value || 0) + (+fee || 0)).toString(),
          currency: token.symbol,
          listCurrency: token.symbol,
          date,
          direction: 'out',
          transferType: 'fee',
          fee,
        };
      } else if (fromAddress === accountAddress && toAddress !== accountAddress && Number(tx.value) === 0) {
        txData = {
          id: `${txId}-${index}`,
          type: txType,
          amount: tx.value?.toString() ?? '0',
          listAmount: (+fee || 0).toString(),
          currency: token.symbol,
          listCurrency: token.symbol,
          date,
          direction: 'out',
          transferType: 'fee',
          fee,
        };
      } else if (toAddress !== accountAddress) {
        txData = {
          id: `${txId}-${index}`,
          type: txType,
          amount: tx.value?.toString() ?? '0',
          listAmount: ((+tx.value || 0) + (+fee || 0)).toString(),
          currency: token.symbol,
          listCurrency: token.symbol,
          date,
          direction: 'out',
          transferType,
          fee,
        };
      }
    } else if (coinType === 'coin' && transferType.toLowerCase() === 'receive') {
      txData = {
        id: `${txId}-${index}`,
        type: txType,
        amount: tx.value?.toString() ?? '0',
        listAmount: tx.value?.toString() ?? '0',
        currency: token.symbol,
        listCurrency: token.symbol,
        date,
        direction: 'in',
        transferType,
        fee,
      };
    } else if (coinType === 'token' && transferType.toLowerCase() === 'withdrawal' && Number(fee) > 0) {
      txData = {
        id: `${txId}-${index}`,
        type: txType,
        amount: tx.value?.toString() ?? '0',
        listAmount: fee.toString(),
        currency: tx.coin_name,
        listCurrency: token.symbol,
        date,
        direction: 'out',
        transferType: 'fee',
        fee,
      };
    }

    // 내부이체 메타데이터 세팅
    if (txData && (rawQPin != null || rawShopName)) {
      txData.isInternal = true;
      if (rawQPin !== null && rawQPin !== undefined && rawQPin !== '') {
        const qNum = Number(rawQPin);
        if (!Number.isNaN(qNum)) {
          txData.qPin = qNum;
        }
      }
      if (rawShopName) {
        txData.shopName = String(rawShopName);
      }
    }

    return txData;
  };

  // ---------------- 맵핑: 토큰 ----------------
  const mapTokenTransactionToWallet = (
    tx: any,
    accountAddress: string,
    index: number,
  ): Wallet => {
    const toAddress = tx.to ? tx.to.hash || tx.to : null;
    const fromAddress = tx.from ? tx.from.hash || tx.from : null;
    let fee = '0';
    const date = new Date(tx.timestamp * 1000).toISOString().split('T')[0];
    const transferType = tx.transfer_type;
    let txId = '';
    let txType = '';
    const coinType = tx.coin_type;

    const rawQPin = tx.seller?.qpin ?? null;
    const rawShopName = tx.seller?.seller_name ?? null;

    if (tx.transaction_hash != null && tx.transaction_hash !== 'undefined') {
      txId = tx.transaction_hash;
    } else {
      txId = tx.hash;
    }

    if (tx.fee?.value != null && tx.fee?.value !== 'undefined') {
      fee = tx.fee.value.toString();
    }

    if (transferType.toLowerCase() === 'withdrawal') {
      txType = i18next.t('template_wallet_screen_message_09');
    } else {
      txType = i18next.t('template_wallet_screen_message_10');
    }

    const txData: Wallet = {
      id: `${txId}-${index}`,
      type: txType,
      amount: tx.value?.toString() ?? '0',
      listAmount: tx.value?.toString() ?? '0',
      currency: token.symbol,
      listCurrency: token.symbol,
      date,
      direction: transferType.toLowerCase() === 'withdrawal' ? 'out' : 'in',
      transferType,
      fee,
    };

    if (rawQPin != null || rawShopName) {
      txData.isInternal = true;
      if (rawQPin !== null && rawQPin !== undefined && rawQPin !== '') {
        const qNum = Number(rawQPin);
        if (!Number.isNaN(qNum)) {
          txData.qPin = qNum;
        }
      }
      if (rawShopName) {
        txData.shopName = String(rawShopName);
      }
    }

    return txData;
  };

  // ---------------- 최초/변경시 TX 요청 ----------------
  useEffect(() => {
    const getServerTxList = async () => {
      setLoading(true);
      await fetchServerTxHistory();
      setLoading(false);
    };

    if (accountAddress && token) {
      getServerTxList();
    }
  }, [accountAddress, token?.symbol]);

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      await fetchServerTxHistory();
      setLoading(false);
    };

    if (accountAddress && token) {
      fetchTransactions();
    }
  }, [accountAddress, token?.symbol]);

  useEffect(() => {
    applyFilters();
  }, [startDate, endDate, selectedTransactionOption]);

  const groupedTransactions = filteredTransactions.reduce((groups, transaction) => {
    const date = transaction.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(transaction);
    return groups;
  }, {} as { [key: string]: Wallet[] });

  const applyFilters = () => {
    const filtered = transactions.filter((tx: any) => {
      const txDate = moment(tx.date, 'YYYY. M. D. A h:mm:ss').toDate();
      const normalizedTxDate: any = new Date(txDate.setHours(0, 0, 0, 0));

      let isWithinDateRange = true;

      if (startDate) {
        const normalizedStartDate = new Date(startDate).setHours(0, 0, 0, 0);
        isWithinDateRange = isWithinDateRange && normalizedTxDate >= normalizedStartDate;
      }

      if (endDate) {
        const normalizedEndDate = new Date(endDate).setHours(23, 59, 59, 999);
        isWithinDateRange = isWithinDateRange && normalizedTxDate <= normalizedEndDate;
      }

      const isTransactionType =
        selectedTransactionOption === i18next.t('template_wallet_screen_message_16') ||
        tx.type === selectedTransactionOption;

      return isWithinDateRange && isTransactionType;
    });

    setFilteredTransactions(filtered);
  };

  const handleDateSubmit = () => {
    setDateModalVisible(false);
    setStartDate(selectedStartDate);
    setEndDate(selectedEndDate);
    applyFilters();
  };

  const handleDateOptionPress = (option: string) => {
    setSelectedDateOption(option);
    const now = new Date();
    let newStartDate = new Date();
    switch (option) {
      case `7${i18next.t('template_wallet_screen_message_12')}`:
        newStartDate.setDate(now.getDate() - 7);
        break;
      case `30${i18next.t('template_wallet_screen_message_12')}`:
        newStartDate.setDate(now.getDate() - 30);
        break;
      case `90${i18next.t('template_wallet_screen_message_12')}`:
        newStartDate.setDate(now.getDate() - 90);
        break;
      case `180${i18next.t('template_wallet_screen_message_12')}`:
        newStartDate.setDate(now.getDate() - 180);
        break;
    }
    setSelectedStartDate(newStartDate);
    setSelectedEndDate(now);
  };

  const handleTransactionOptionPress = (option: any) => {
    setSelectedTransactionOption(option);
    setTransactionModalVisible(false);
    applyFilters();
  };

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchServerTxHistory();
    setTimeout(() => {
      setRefreshing(false);
    }, 2000);
  }, []);

  // ---------------- 렌더링 ----------------
  const renderTransaction = ({ item }: { item: Wallet }) => {
    const hasSellerMeta = item.qPin != null && !!item.shopName;
    const listAmountStr = item.listAmount || '0';

    return (
      <div style={{ ...styles.transactionItem, backgroundColor: theme.card_background_06 }}>
        <div style={{ display: 'flex' }}>
          <img
            src={item.direction === 'in' ? './img/in_icon.png' : './img/out_icon.png'}
            alt={item.direction === 'in' ? 'in transaction' : 'out transaction'}
            style={styles.transactionIcon}
          />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <CustomText style={{ ...styles.transactionName, color: theme.label_strong }}>
              {item.transferType !== 'fee'
                ? item.direction === 'in'
                  ? t('template_wallet_screen_message_10')
                  : t('template_wallet_screen_message_09')
                : t('template_wallet_screen_message_26')}
            </CustomText>
            {hasSellerMeta && (
              <CustomText style={styles.transactionSubInfo}>
                {`[${item.qPin ?? ''}]${item.shopName ?? ''}`}
              </CustomText>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={styles.transactionAmountContainer}>
            <CourierText
              weight="bold"
              style={{
                ...styles.transactionAmount,
                color: item.direction === 'in' ? theme.text_18 : theme.text_07,
              }}
            >
              {Number(listAmountStr) !== 0 ? (item.direction === 'in' ? '+' : '-') : ''}
              {syncListFormatNumberWithCommas(new BigNumber(listAmountStr))}
            </CourierText>
            <CustomText style={styles.transactionSymbol}>
              {item?.listCurrency?.toUpperCase()}
            </CustomText>
          </div>
        </div>
      </div>
    );
  };

  const renderTransactionGroup = ({ item: date }: { item: string }) => (
    <div style={styles.transactionGroup}>
      <CustomText style={{ ...styles.transactionDate, color: theme.text_05 }}>
        {date}
      </CustomText>
      {groupedTransactions[date].map((transaction) => (
        <div
          key={transaction.id}
          onClick={() => navigate('/WalletScreenDetail', { state: { item: transaction } })}
          style={{ cursor: 'pointer' }}
        >
          {renderTransaction({ item: transaction })}
        </div>
      ))}
    </div>
  );

  const renderSkeleton = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Spin size="large" />
    </div>
  );

  const renderDateModal = () => (
    <div style={styles.bottomModal}>
      <div style={styles.dateModalContent}>
        <div style={styles.modalHeader}>
          <CustomText weight="medium" style={styles.modalTitle}>
            {t('template_wallet_screen_message_11')}
          </CustomText>
          <Button onClick={() => setDateModalVisible(false)} style={styles.closeButton}>
            <X name="close" size={20} color={theme.text_01} />
          </Button>
        </div>

        <div style={styles.dateOptions}>
          {[
            `7${i18next.t('template_wallet_screen_message_12')}`,
            `30${i18next.t('template_wallet_screen_message_12')}`,
            `90${i18next.t('template_wallet_screen_message_12')}`,
            `180${i18next.t('template_wallet_screen_message_12')}`,
          ].map((option) => (
            <Button
              key={option}
              onClick={() => handleDateOptionPress(option)}
              style={{
                ...styles.dateOption,
                backgroundColor: selectedDateOption === option ? 'blue' : 'white',
                color: selectedDateOption === option ? 'white' : 'black',
              }}
            >
              {option}
            </Button>
          ))}
        </div>

        <div style={styles.dateInputContainer}>
          <DatePicker
            value={selectedStartDate.toISOString().split('T')[0]}
            onChange={(date: any) => setSelectedStartDate(date!)}
            placeholder="시작 날짜"
          />
          <span style={styles.dateSeparator}>~</span>
          <DatePicker
            value={selectedEndDate.toISOString().split('T')[0]}
            onChange={(date: any) => setSelectedEndDate(date!)}
            placeholder="종료 날짜"
          />
        </div>

        <CustomText style={{ ...styles.dateInfo, color: 'gray' }}>
          {t('template_wallet_screen_message_13')}
        </CustomText>

        <CustomButton
          containerStyle={{ ...styles.dateSubmitButton, backgroundColor: 'blue' }}
          textStyle={styles.dateSubmitButtonText}
          onPress={handleDateSubmit}
          text={t('template_wallet_screen_message_14')}
          weight="medium"
          withBackground={true}
          backgroundColor="blue"
        />
      </div>
    </div>
  );

  const renderTransactionModal = () => (
    <div style={styles.bottomModal}>
      <div style={styles.tradeModalContent}>
        <div style={styles.modalHeader}>
          <CustomText weight="medium" style={styles.modalTitle}>
            {i18next.t('template_wallet_screen_message_15')}
          </CustomText>
          <button
            onClick={() => setTransactionModalVisible(false)}
            style={{ background: 'none', border: 'none' }}
          >
            <X name="close" size={20} color={theme.text_01} />
          </button>
        </div>
        {[
          i18next.t('template_wallet_screen_message_16'),
          i18next.t('template_wallet_screen_message_10'),
          i18next.t('template_wallet_screen_message_09'),
        ].map((option) => (
          <div
            key={option}
            style={styles.modalOption}
            onClick={() => handleTransactionOptionPress(option)}
          >
            <CustomText
              weight={selectedTransactionOption === option ? 'semi' : undefined}
              style={{
                ...styles.modalOptionText,
                ...(selectedTransactionOption === option
                  ? { color: theme.primary_normal, fontSize: 18 }
                  : {}),
              }}
            >
              {option}
            </CustomText>
            {selectedTransactionOption === option && (
              <Check name="checkmark" size={20} color={theme.primary_normal} />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderStatusModal = () => (
    <div style={styles.bottomModal}>
      <div style={styles.statusModalContent}>
        <div style={styles.modalHeader}>
          <CustomText weight="medium" style={styles.modalTitle}>
            {i18next.t('template_wallet_screen_message_17')}
          </CustomText>
          <button
            onClick={() => setStatusModalVisible(false)}
            style={{ background: 'none', border: 'none' }}
          >
            <X name="close" size={20} color={theme.text_01} />
          </button>
        </div>
        {[
          i18next.t('template_wallet_screen_message_17'),
          i18next.t('template_wallet_screen_message_18'),
          i18next.t('template_wallet_screen_message_19'),
          i18next.t('template_wallet_screen_message_20'),
          i18next.t('template_wallet_screen_message_21'),
        ].map((option) => (
          <div key={option} style={styles.modalOption} onClick={() => setSelectedStatusOption(option)}>
            <CustomText
              weight={selectedStatusOption === option ? 'semi' : undefined}
              style={{
                ...styles.modalOptionText,
                ...(selectedStatusOption === option
                  ? { color: theme.primary_normal, fontSize: 18 }
                  : {}),
              }}
            >
              {option}
            </CustomText>
            {selectedStatusOption === option && (
              <Check name="checkmark" size={20} color={theme.primary_normal} />
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const syncFormatNumberWithCommas = (input: BigNumber, decimalPlaces: number = 9): string => {
    if (input === null || input.isNaN()) {
      return '0.' + '0'.repeat(decimalPlaces);
    }

    const absInput = input.abs();
    const scale = new BigNumber(10).pow(decimalPlaces);
    const floored = absInput
      .multipliedBy(scale)
      .integerValue(BigNumber.ROUND_FLOOR)
      .dividedBy(scale);
    const fixedStr = floored.toFixed(decimalPlaces);

    let [intPart, decPart] = fixedStr.split('.');
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (decPart) {
      decPart = decPart.replace(/0+$/, '');
      if (decPart.length === 0) {
        return intPart + '.00';
      }
      return intPart + '.' + decPart;
    }
    return intPart + '.00';
  };

  const syncListFormatNumberWithCommas = (input: BigNumber, decimalPlaces: number = 9): string => {
    if (input === null || input.isNaN()) {
      return '0.' + '0'.repeat(decimalPlaces);
    }

    const absInput = input.abs();

    const units = [
      { value: new BigNumber(1e12), symbol: 'T' },
      { value: new BigNumber(1e9), symbol: 'B' },
      { value: new BigNumber(1e6), symbol: 'M' },
    ];

    const unit = units.find((u) => absInput.isGreaterThanOrEqualTo(u.value));
    if (unit) {
      const scaled = absInput.dividedBy(unit.value);
      const decimals = 2;
      const scale2 = new BigNumber(10).pow(decimals);
      const truncated = scaled
        .multipliedBy(scale2)
        .integerValue(BigNumber.ROUND_FLOOR)
        .dividedBy(scale2);
      const formatted = truncated.toFixed(decimals);
      return formatted + unit.symbol;
    }

    const integerPart = absInput.integerValue(BigNumber.ROUND_FLOOR);
    const integerStr = integerPart.toString();
    let effectiveDecimals = decimalPlaces;
    if (integerStr.length > 4) {
      effectiveDecimals = Math.max(0, decimalPlaces - (integerStr.length - 4));
    }

    const scale = new BigNumber(10).pow(effectiveDecimals);
    const floored = absInput
      .multipliedBy(scale)
      .integerValue(BigNumber.ROUND_FLOOR)
      .dividedBy(scale);
    const fixedStr = floored.toFixed(effectiveDecimals).replace(/\.?0+$/, '');

    let [intPart, decPart] = fixedStr.split('.');
    intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return intPart + (decPart ? '.' + decPart : '');
  };

  const getDynamicFontSize = (number: string): number => {
    const length = number.length;
    if (length <= 20) return 28;
    if (length <= 25) return 24;
    if (length <= 30) return 20;
    if (length <= 35) return 18;
    if (length <= 40) return 16;
    return 14;
  };

  const styles = {
    container: {
      backgroundColor: theme.background_03,
      padding: '60px 0px 24px',
    } as React.CSSProperties,
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 15,
    } as React.CSSProperties,
    headerTitle: {
      fontSize: 16,
      fontWeight: 'bold',
    } as React.CSSProperties,
    tabsContainer: {
      display: 'flex',
      alignItems: 'center',
    } as React.CSSProperties,
    tab: {
      cursor: 'pointer',
    } as React.CSSProperties,
    tabText: {
      color: theme.text_01,
      fontSize: 14,
      margin: '0 5px',
    } as React.CSSProperties,
    tabDivider: {
      width: 1,
      height: 10,
      backgroundColor: theme.divider_background_02,
      margin: '0 10px',
    } as React.CSSProperties,
    balanceText: {
      fontSize: 28,
      textAlign: 'right',
      transition: 'font-size 0.3s ease',
      height: 'auto',
    } as React.CSSProperties,
    filterContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
    } as React.CSSProperties,
    filterOptions: {
      display: 'flex',
      alignItems: 'center',
    } as React.CSSProperties,
    filterText: {
      fontSize: 14,
      margin: '0 5px',
    } as React.CSSProperties,
    filterIcon: {
      width: 20,
      height: 16,
    } as React.CSSProperties,
    transactionHistoryContainer: {
      borderTopLeftRadius: 10,
      borderTopRightRadius: 10,
      padding: '24px',
      height: 'calc(100vh - 220px)',
      overflow: 'auto',
    } as React.CSSProperties,
    transactionList: {
      marginTop: 10,
    } as React.CSSProperties,
    transactionGroup: {
      padding: '20px 0 0',
      borderRadius: 10,
    } as React.CSSProperties,
    transactionItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 15px',
      backgroundColor: theme.card_background_04,
      borderRadius: 10,
      marginBottom: 10,
      border: `1px solid ${theme.border_04}`,
      width: '100%',
      boxSizing: 'border-box',
      justifyContent: 'space-between',
    } as React.CSSProperties,
    transactionIcon: {
      width: 24,
      height: 24,
      marginRight: 10,
    } as React.CSSProperties,
    transactionName: {
      fontSize: 15,
      paddingTop: '3px',
    } as React.CSSProperties,
    transactionSubInfo: {
      fontSize: 12,
      color: theme.text_10,
      marginTop: 2,
    } as React.CSSProperties,
    transactionAmountContainer: {
      display: 'flex',
      alignItems: 'center',
    } as React.CSSProperties,
    transactionAmount: {
      fontSize: 16,
    } as React.CSSProperties,
    transactionSymbol: {
      color: theme.text_06,
      fontSize: 12,
      marginLeft: 5,
    } as React.CSSProperties,
    transactionDate: {
      fontSize: 14,
      color: theme.text_10,
      marginBottom: 15,
    } as React.CSSProperties,
    dateModalContent: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: theme.background_02,
      padding: 20,
      borderRadius: 20,
      boxSizing: 'border-box',
      width: '90%',
    } as React.CSSProperties,
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      alignItems: 'center',
      marginBottom: 20,
      padding: '0 10px',
      boxSizing: 'border-box',
    } as React.CSSProperties,
    modalTitle: {
      fontSize: 16,
      color: theme.text_01,
    } as React.CSSProperties,
    dateOptions: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      marginBottom: 20,
    } as React.CSSProperties,
    dateOption: {
      padding: '15px 25px',
      borderRadius: 8,
      alignItems: 'center',
    } as React.CSSProperties,
    dateInputContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginVertical: 10,
      width: '100%',
    } as React.CSSProperties,
    dateSeparator: {
      fontSize: 18,
      margin: '0 10px',
    } as React.CSSProperties,
    dateInfo: {
      fontSize: 14,
      color: theme.text_03,
      marginTop: '5px',
      marginBottom: '20px',
    } as React.CSSProperties,
    dateSubmitButton: {
      backgroundColor: theme.button_02,
      padding: 12,
      borderRadius: 28,
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    } as React.CSSProperties,
    dateSubmitButtonText: {
      color: 'white',
      fontSize: 18,
    } as React.CSSProperties,
    bottomModal: {
      display: 'flex',
      margin: 0,
      width: '100%',
      height: '100%',
      position: 'fixed',
      top: '0px',
      left: '0px',
      background: theme.background_alternative,
    } as React.CSSProperties,
    tradeModalContent: {
      width: '100%',
      position: 'absolute',
      bottom: '0px',
      left: '0px',
      backgroundColor: theme.background_02,
      padding: 20,
      borderTopLeftRadius: 30,
      borderTopRightRadius: 30,
      boxSizing: 'border-box',
    } as React.CSSProperties,
    modalOption: {
      padding: '20px 10px',
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxSizing: 'border-box',
    } as React.CSSProperties,
    modalOptionText: {
      fontSize: 16,
      color: theme.text_01,
    } as React.CSSProperties,
    statusModalContent: {
      width: '100%',
      position: 'absolute',
      bottom: '60px',
      left: '0px',
      backgroundColor: 'white',
      padding: 20,
      borderTopLeftRadius: 30,
      borderTopRightRadius: 30,
      boxSizing: 'border-box',
    } as React.CSSProperties,
    optionPressableContainer: {
      display: 'flex',
      alignItems: 'center',
    } as React.CSSProperties,
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '24px',
      color: theme.text_01,
      display: 'flex',
    } as React.CSSProperties,
  };

  return (
    <>
      <MainPageHeader />
      <div className="container" style={{ ...styles.container, backgroundColor: theme.background_normal }}>
        <div style={{ width: '100%' }}>
          <div style={{ padding: '0px 24px 24px 24px' }}>
            <div style={styles.header}>
              <CustomText style={{ ...styles.headerTitle, color: theme.label_strong }}>
                {token?.symbol?.toUpperCase() || ''}
              </CustomText>
              <div style={styles.tabsContainer}>
                <button
                  onClick={() => navigate('/WithdrawScreen', { state: { token } })}
                  style={styles.tab}
                >
                  <CustomText style={{ ...styles.tabText, color: theme.label_strong }}>
                    {t('template_wallet_screen_message_02')}
                  </CustomText>
                </button>
                <div style={styles.tabDivider}></div>
                <button
                  onClick={() => navigate('/ReceiveScreen', { state: { token } })}
                  style={styles.tab}
                >
                  <CustomText style={{ ...styles.tabText, color: theme.label_strong }}>
                    {t('template_wallet_screen_message_03')}
                  </CustomText>
                </button>
              </div>
            </div>
            {token && (
              <CourierText
                weight="bold"
                style={{
                  ...styles.balanceText,
                  color: theme.label_strong,
                  fontSize: getDynamicFontSize(
                    syncFormatNumberWithCommas(new BigNumber(token.balance.toString()), 9),
                  ),
                }}
              >
                {syncFormatNumberWithCommas(new BigNumber(token.balance.toString()), 9)}
              </CourierText>
            )}
          </div>
          <div
            className="containerScroll"
            style={{ ...styles.transactionHistoryContainer, backgroundColor: theme.background_10 }}
          >
            <div style={styles.filterContainer}>
              <CustomText style={{ ...styles.headerTitle, color: theme.label_strong }}>
                {t('template_wallet_screen_message_05')}
              </CustomText>
              <div style={styles.filterOptions}>
                <button style={styles.optionPressableContainer} onClick={() => setDateModalVisible(true)}>
                  <CustomText style={{ ...styles.filterText, color: theme.label_strong }}>
                    {t('template_wallet_screen_message_06')}
                  </CustomText>
                  <img src="./img/arrow_down_icon.png" alt="arrow" style={styles.filterIcon} />
                </button>

                <button
                  style={styles.optionPressableContainer}
                  onClick={() => setTransactionModalVisible(true)}
                >
                  <CustomText style={{ ...styles.filterText, color: theme.label_strong }}>
                    {t('template_wallet_screen_message_07')}
                  </CustomText>
                  <img src="./img/arrow_down_icon.png" alt="arrow" style={styles.filterIcon} />
                </button>
              </div>
            </div>

            {loading ? (
              renderSkeleton()
            ) : (
              <div style={styles.transactionList}>
                {Object.keys(groupedTransactions).length > 0 ? (
                  Object.keys(groupedTransactions).map((date) => (
                    <div key={date}>{renderTransactionGroup({ item: date })}</div>
                  ))
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 'calc(100vh - 500px)',
                      color: theme.text_01,
                      marginTop: '50px',
                    }}
                  >
                    <CustomText style={{ color: theme.text_03 }}>
                      {t('template_wallet_screen_message_25')}
                    </CustomText>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {isDateModalVisible && renderDateModal()}
        {isTransactionModalVisible && renderTransactionModal()}
        {/* {isStatusModalVisible && renderStatusModal()} */}
      </div>
    </>
  );
};

export default WalletScreen;
